import { useId, type ReactNode } from "react";

import { cn } from "@one-nexty/components-web";

import { useCopy } from "../lib/use-copy";
import { CopyIcon } from "./icons";

interface PreviewProps {
  /** Nom du fichier affiché en tête du volet de code. */
  label?: string;
  children: ReactNode;
  /** `stack` empile verticalement (formulaires, listes) au lieu d'aligner en ligne. */
  layout?: "row" | "stack";
  /** Extrait JSX correspondant à la démonstration. */
  code?: string;
  className?: string;
}

/**
 * Cadre de démonstration : le rendu au-dessus, son code juste en dessous.
 *
 * Le volet est un `<details>` natif, ce qui lui donne gratuitement le clavier,
 * l'état `:open` en CSS pur — donc une bascule sans re-rendu React — et la
 * recherche dans la page : Ctrl+F sur un nom de prop ouvre le volet qui le
 * contient.
 */
export function Preview({ label, children, layout = "row", code, className }: PreviewProps) {
  return (
    <div className="relative z-0 overflow-hidden rounded-xl border border-border bg-surface-raised">
      <div
        className={cn(
          "p-6",
          layout === "row" ? "flex flex-wrap items-center gap-3" : "flex flex-col gap-4",
          className,
        )}
      >
        {children}
      </div>

      {code && <CodePane code={code} label={label} />}
    </div>
  );
}

/** Volet de code repliable, réutilisable hors d'un `Preview`. */
export function CodePane({ code, label }: { code: string; label?: string }) {
  const { copy, copied } = useCopy();
  const source = code.trim();
  const id = useId();

  return (
    <details
      data-nexty-accordion=""
      className="group border-t border-border bg-surface-sunken/40"
    >
      <summary
        className={cn(
          "flex cursor-pointer list-none items-center gap-2 px-4 py-2 text-xs font-medium text-fg-muted",
          "transition-colors duration-fast ease-standard hover:text-fg",
          "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
          "[&::-webkit-details-marker]:hidden",
        )}
        aria-controls={id}
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="size-3.5 transform-gpu transition-transform duration-fast ease-standard group-open:rotate-90"
        >
          <path d="m8 6 4 4-4 4" />
        </svg>
        Code
        {label && <span className="ml-auto font-mono text-[11px] text-fg-subtle">{label}</span>}
      </summary>

      <div id={id} className="relative border-t border-border">
        <button
          type="button"
          onClick={() => copy(source)}
          aria-label="Copier le code"
          className={cn(
            "absolute top-2.5 right-2.5 z-10 inline-flex items-center gap-1.5 rounded-md border border-border",
            "bg-surface-raised px-2 py-1 font-mono text-[11px] text-fg-muted",
            "transition-colors duration-fast ease-standard hover:text-fg",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          )}
        >
          <CopyIcon className="size-3" />
          {copied === source ? "Copié" : "Copier"}
        </button>

        <pre className="overflow-x-auto p-4 pr-24 font-mono text-xs leading-relaxed text-fg">
          <code>{source}</code>
        </pre>
      </div>
    </details>
  );
}
